import axios from 'axios';
import { useMutation } from 'react-query';
import { QueryConfig } from '../react-query-utils';
import { useAzureToken } from './getAzureSpeechToken';
import { LanguageCode } from '@visikon/core-models/languageTypes';
import { findVoiceById, findVoiceByNameAndLanguage, getDefaultVoiceForLanguage } from 'containers/composer/block/text2speech/StaticVoiceData';
import { logger } from '@sentry/react';
import { XMLParser } from 'fast-xml-parser';
import { PHONETICS_FILE_URL } from 'api/phoneticsApi';
import { isSayAsLexeme, LexemeEntry, LexiconXML } from '@visikon/core-models';

const AZURE_API = 'https://northeurope.tts.speech.microsoft.com/cognitiveservices/v1';

function getSpeechRateKey(language: LanguageCode, voiceName: string) {
  return `speechRate-${language}-${voiceName}`;
}

export function getSpeechRateForVoice(activeLanguage: LanguageCode, selectedVoiceId?: string) {
  if (!selectedVoiceId) {
    return 0;
  }

  const rateKey = getSpeechRateKey(activeLanguage, selectedVoiceId);
  const rate = localStorage.getItem(rateKey);

  if (rate) {
    return parseInt(rate, 10) || 0;
  }

  return 0;
}

export function setSpeechRateForVoice(activeLanguage: LanguageCode, voice: string, rate: number) {
  const rateKey = getSpeechRateKey(activeLanguage, voice);
  localStorage.setItem(rateKey, rate.toString());
}

// Ported from Self Service Platform code base
async function loadLexicon(textLang: LanguageCode): Promise<Map<string, LexemeEntry[]>> {
  const url = PHONETICS_FILE_URL(textLang);
  const res = await fetch(url, {
    cache: 'no-cache', // Allow caching, but revalidate on each request
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch lexicon from ${url}: ${res.statusText}`);
  }
  const xml = await res.text();

  const parser = new XMLParser({
    ignoreAttributes: false,
    // Do not try to parse values as a numeric value, which causes issues with number lexeme entries (eg. 1 og 9)
    // We don't have any numeric values in the lexicon
    parseTagValue: false,
    isArray(tagName) {
      return tagName === 'lexeme';
    },
  });
  const parsed = parser.parse(xml) as LexiconXML;

  const fetchedLexiconMap: Map<string, LexemeEntry[]> = new Map();
  const lexemes = parsed.lexicon.lexeme;

  if (lexemes) {
    for (const lexeme of lexemes) {
      if (typeof lexeme.grapheme === 'string') {
        const word_lowercase = lexeme.grapheme.toLowerCase();
        const entries = fetchedLexiconMap.get(word_lowercase) || [];
        entries.push(lexeme);
        fetchedLexiconMap.set(word_lowercase, entries);
      }
    }
  }

  return fetchedLexiconMap;
}

/**
 * Regex to capture word components for phoneme replacement.
 * Captures three groups:
 * 1. Leading characters like brackets, parentheses, quotes & apostrophes (`([("']`)
 * 2. Core word (Unicode aware letters & digits, multiple internal hyphens but not trailing)
 * 3. Trailing characters like: periods, commas, exclamation marks, question marks, semicolons, colons, hyphen, em dashes, quotes & apostrophes (`[.,!?;:)\]—"'-]`)
 */
// Note: The hyphen must be at the end of the character class to be treated as a literal
const WORD_COMPONENTS_REGEX = /^([[("']*)([\p{L}\d]+(?:-+[\p{L}\d]+)*)([.,!?;:)\]—"'-]*)$/u;

export function enhanceWordWithPhoneme(word: string, lexicon: Map<string, LexemeEntry[]>): string {
  const wordComponents = word.match(WORD_COMPONENTS_REGEX);

  if (!wordComponents) {
    logger.warn(`Could not parse word for lexicon lookup: "${word}"`);
    return escapeXml(word);
  }

  const [, leadingMatch, wordClean, trailingMatch] = wordComponents;

  // No phoneme found
  const lexeme = lexicon.get(wordClean.toLowerCase())?.at(0);
  if (!lexeme) return escapeXml(word);

  if (isSayAsLexeme(lexeme)) {
    return `${escapeXml(leadingMatch)}<sub alias="${escapeXml(lexeme.sayAs)}">${escapeXml(wordClean)}</sub>${escapeXml(trailingMatch)}`;
  }

  return `${escapeXml(leadingMatch)}<phoneme alphabet="ipa" ph="${escapeXml(lexeme.phoneme)}">${escapeXml(wordClean)}</phoneme>${escapeXml(trailingMatch)}`;
}

// Lexicon values come from parsed XML (entities decoded), so they must be re-escaped before being embedded in SSML
function escapeXml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

export async function textToSsml(
  text: string,
  synthesisVoiceName: string,
  language: LanguageCode,
  speechRate: number,
  lexicon: Map<string, LexemeEntry[]>,
): Promise<string> {
  // const regex = /\s(Object.keys(map).join('|'))\s/g;
  const replaced = text
    .split(/\s/g)
    .map((word) => enhanceWordWithPhoneme(word, lexicon))
    .join(' ');

  const maximizedLocale = new Intl.Locale(language).maximize();
  const languageWithRegion = `${maximizedLocale.language}-${maximizedLocale.region}`;

  return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="da-DK">
  <voice name="${synthesisVoiceName}">
    <lang xml:lang="${languageWithRegion}">
      <prosody rate="${speechRate}%">
        ${replaced}
      </prosody>
    </lang>
  </voice>
</speak>`;
}

export function useFetchSynthesizedAudio(request: {
  text: string;
  activeLanguage: LanguageCode;
  selectedVoiceId?: string;
  lexiconOverride?: Map<string, LexemeEntry[]>;
  onSuccess?: (audioBlob: Blob) => void;
}) {
  const defaultVoice = getDefaultVoiceForLanguage(request.activeLanguage);
  const selectedVoiceDefinition =
    (request.selectedVoiceId && findVoiceById(request.selectedVoiceId)) ||
    (request.selectedVoiceId && findVoiceByNameAndLanguage(request.selectedVoiceId, request.activeLanguage)) ||
    defaultVoice;

  if (!selectedVoiceDefinition) {
    throw new Error(`No voices configured for language: ${request.activeLanguage}`);
  }

  const voiceId = selectedVoiceDefinition.voice;
  const lang = request.activeLanguage;

  const newAzureToken = useAzureToken();

  return useMutation<Blob>(['synthesizedAudio', request.text, voiceId], {
    mutationFn: async () => {
      const azureToken = await newAzureToken();
      const azureHeaders = {
        'X-Microsoft-OutputFormat': 'riff-48khz-16bit-mono-pcm',
        'Content-Type': 'application/ssml+xml',
        Authorization: `Bearer ${azureToken}`,
      };

      const speechRate = getSpeechRateForVoice(lang, voiceId);
      const loadedLexicon = await loadLexicon(request.activeLanguage);
      const lexicon = request.lexiconOverride ? new Map([...loadedLexicon, ...request.lexiconOverride]) : loadedLexicon;
      const ssml = await textToSsml(request.text, voiceId, lang, speechRate, lexicon);
      const res = await axios.post(AZURE_API, ssml, {
        headers: { ...azureHeaders },
        responseType: 'blob',
      });

      return res.data;
    },
    onSuccess: request.onSuccess,
    ...QueryConfig,
  });
}
